import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { CalendarDateRequired, toJSDate, toJSDateUTC } from '#utils/UNSAFE_calendarDateUtils';
import { weekNumberCellStyles, weekNumberStyles } from './themes/WeekNumberCellStyle.css';
import * as Logger from '../utils/UNSAFE_logger';

type Props = {
  /**
   * startDateOfWeek is the first date of the week row
   */
  startDateOfWeek: CalendarDateRequired;
};

/**
 * The ISO 8601 definition for week 01 is the week with the first Thursday of the Gregorian year (i.e. of January) in it.
 * https://en.wikipedia.org/wiki/ISO_week_date
 * @param date The first date of the week row to be used to calculate the week number.
 * The date must be a Saturday, Sunday, or Monday. We assume this because the week for
 * all the locales we support start on one of these days.
 * @returns the number of week in the year
 */
const calculateWeekNumber = (date: CalendarDateRequired) => {
  const checkDate = toJSDateUTC(date.year, date.month, date.day);
  const dayNumber = checkDate.getUTCDay(); // 0 for Sunday, 1 for Monday, 2 for Tuesday, etc.

  if (dayNumber !== 0 && dayNumber !== 1 && dayNumber !== 6) {
    // This message is for internal JET developers. We should never see this message. In all locales the day of the
    // week starts on Sunday, Monday or Saturday.
    Logger.warn(`calculateWeekNumber's date property's getUTCDay() is assumed to be 0 (Sunday), 1 (Monday) or 6 (Saturday)
      since that is the first day of the week for the locale's JET supports.`);
  }

  // For ISO week, we set the start of the week to Monday
  if (dayNumber !== 1) {
    // If it is already Monday, do nothing.
    // If it is Sunday (0), add 1 to move it up to Monday.
    // If it is Saturday (6), move it up two days to Monday.
    checkDate.setUTCDate(checkDate.getUTCDate() + (dayNumber === 0 ? 1 : 2));
  }

  // Find Thursday (day value of 4) of this week. Since we know checkDate at this point is a Monday,
  // we move it up 3 days.
  checkDate.setUTCDate(checkDate.getUTCDate() + 3);

  // Create the start of the year date
  const janFirst = toJSDate(checkDate.getUTCFullYear(), 1, 1);

  return Math.floor(Math.round((checkDate.getTime() - janFirst.getTime()) / 86400000) / 7) + 1;
};

/**
 * The WeekNumberCell is the cell at the start of a week's row within the DatePicker's
 * month view that contains the week's number within the year. This displays when
 * the DatePicker's 'weekDisplay' prop is set to 'number'.
 * This component will format the week number for the aria-label.
 */
const WeekNumberCell = ({ startDateOfWeek }: Props) => {
  const weekNumber = calculateWeekNumber(startDateOfWeek);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  return (
    <div
      aria-label={translations.datePicker_weekNum({ NUM: String(weekNumber) })}
      class={weekNumberCellStyles}
      role="rowheader">
      <span class={weekNumberStyles}>{weekNumber}</span>
    </div>
  );
};

export { WeekNumberCell };
